import { useQuery } from '@tanstack/react-query';

import {
  useGetApiV5FirmsContactsGroupedByAccount,
  FirmContactV5,
  FirmAccountWithContactCount,
} from '@liscio/api';

import apiClient from '../api-client';

export interface FirmContactWithDisplay extends FirmContactV5 {
  displayName: string;
  initials: string;
}

/**
 * Helper function to format firm contact for display
 */
export function formatFirmContactForDisplay(
  contact: FirmContactV5
): FirmContactWithDisplay {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  const displayName = `${firstName} ${lastName}`.trim();
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((name) => name.charAt(0).toUpperCase())
    .join('');

  return { ...contact, displayName, initials };
}

/**
 * Hook to fetch v5 firms contacts with optional keyword search
 * @param keyword - Optional search keyword to filter contacts
 * @param enabled - Whether the query should be enabled
 */
export function useFirmsContactsV5(keyword?: string, enabled: boolean = true) {
  return useQuery<FirmContactV5[]>({
    queryKey: ['firmsContactsV5', keyword],
    queryFn: async () => {
      const response = await apiClient.contactsV5Firms.getFirmsContacts(
        keyword
      );
      return response.data;
    },
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to search accounts with nested contacts + employees grouped by account.
 * Primary hook for the redesigned AccountContactSelector.
 */
export function useFirmsContactsGroupedByAccount(
  keyword: string,
  enabled: boolean = true,
  purpose: 'search' | 'account-options' = 'search',
  accountId?: number,
  includeAccountless: boolean = false
) {
  const trimmed = keyword.trim();

  return useGetApiV5FirmsContactsGroupedByAccount(
    {
      keyword: trimmed,
      account_id: accountId,
      include_accountless: includeAccountless || undefined,
    },
    {
      query: {
        queryKey: [
          'firmsContactsGroupedByAccount',
          purpose,
          accountId,
          includeAccountless,
          trimmed,
        ],
        enabled,
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000,
        select: (response) =>
          response.data ?? {
            accounts: [],
            employees: [],
            accountless_contacts: [],
          },
      },
    }
  );
}

/**
 * Hook to fetch list of accounts with contact counts
 * Used in the redesigned AccountContactSelector
 */
export function useFirmsAccountsList() {
  return useQuery<FirmAccountWithContactCount[]>({
    queryKey: ['firmsAccountsList'],
    queryFn: async () => {
      const response = await apiClient.contactsV5Firms.getAccountsList();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch contacts for selected account IDs plus all active employees
 * Used in the redesigned AccountContactSelector
 * @param accountIds - Array of account IDs
 * @param enabled - Whether the query should be enabled
 */
export function useFirmsContactsForAccounts(
  accountIds: number[],
  enabled: boolean = true
) {
  return useQuery<FirmContactV5[]>({
    queryKey: ['firmsContactsForAccounts', accountIds],
    queryFn: async () => {
      if (!accountIds || accountIds.length === 0) {
        return [];
      }
      const response = await apiClient.contactsV5Firms.getContactsForAccounts(
        accountIds
      );
      return response.data;
    },
    enabled: enabled && accountIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to search contacts and employees by keyword
 * Used in the redesigned AccountContactSelector
 * @param keyword - Search keyword
 * @param enabled - Whether the query should be enabled
 */
export function useFirmsContactsSearch(
  keyword: string,
  enabled: boolean = true
) {
  return useQuery<FirmContactV5[]>({
    queryKey: ['firmsContactsSearch', keyword],
    queryFn: async () => {
      if (!keyword || keyword.trim().length === 0) {
        return [];
      }
      const response = await apiClient.contactsV5Firms.searchContacts(keyword);
      return response.data;
    },
    enabled: enabled && keyword.trim().length > 0,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}
